import type { SiteLanguage } from "./platform";
import { DESTINATION_ROUTES, getLocalizedPath } from "./destinationRoutes";

export type DestinationStatus = "active" | "overview" | "coming_soon";

export interface Destination {
  id: string;
  status: DestinationStatus;
  flag: string;
  region: "eu" | "other";
  /** First market with full content + toolkit */
  isFlagship: boolean;
  /** Anchor on /pathways for overview markets (e.g. pathways-spain) */
  hubSectionId?: string;
  visasPath: Record<SiteLanguage, string>;
}

/** Markets — Portugal flagship; ES/IT/GR pathway overviews on /pathways */
export const DESTINATIONS: Destination[] = [
  {
    id: "portugal",
    status: "active",
    flag: "PT",
    region: "eu",
    isFlagship: true,
    visasPath: {
      en: DESTINATION_ROUTES.portugal.visas.all.en,
      pt: DESTINATION_ROUTES.portugal.visas.all.pt,
    },
  },
  {
    id: "spain",
    status: "overview",
    flag: "ES",
    region: "eu",
    isFlagship: false,
    hubSectionId: "pathways-spain",
    visasPath: { en: "/pathways", pt: "/pathways" },
  },
  {
    id: "italy",
    status: "overview",
    flag: "IT",
    region: "eu",
    isFlagship: false,
    hubSectionId: "pathways-italy",
    visasPath: { en: "/pathways", pt: "/pathways" },
  },
  {
    id: "greece",
    status: "overview",
    flag: "GR",
    region: "eu",
    isFlagship: false,
    hubSectionId: "pathways-greece",
    visasPath: { en: "/pathways", pt: "/pathways" },
  },
];

export const TOOLKIT_ITEM_IDS = [
  "quiz",
  "calculator",
  "map",
  "eligibility",
  "advisor",
] as const;

export type ToolkitItemId = (typeof TOOLKIT_ITEM_IDS)[number];

export interface ToolkitItemConfig {
  id: ToolkitItemId;
  href: Record<SiteLanguage, string>;
  /** Optional visa pillar interlink shown below the main CTA */
  relatedVisa?: {
    labelKey: string;
    href: Record<SiteLanguage, string>;
  };
}

export const TOOLKIT_ITEMS: ToolkitItemConfig[] = [
  {
    id: "quiz",
    href: { en: "/interactive", pt: "/interactive" },
    relatedVisa: {
      labelKey: "toolkit.related.pathwaysHub",
      href: { en: "/pathways", pt: "/pathways" },
    },
  },
  {
    id: "calculator",
    href: { en: "/blog/custo-vida", pt: "/blog/custo-vida" },
    relatedVisa: {
      labelKey: "toolkit.related.d7",
      href: {
        en: DESTINATION_ROUTES.portugal.visas.d7.en,
        pt: DESTINATION_ROUTES.portugal.visas.d7.pt,
      },
    },
  },
  {
    id: "map",
    href: { en: "/interactive", pt: "/interactive" },
    relatedVisa: {
      labelKey: "toolkit.related.d8",
      href: {
        en: DESTINATION_ROUTES.portugal.visas.d8.en,
        pt: DESTINATION_ROUTES.portugal.visas.d8.pt,
      },
    },
  },
  {
    id: "eligibility",
    href: {
      en: DESTINATION_ROUTES.portugal.visas.eligibility!.en,
      pt: DESTINATION_ROUTES.portugal.visas.eligibility!.pt,
    },
    relatedVisa: {
      labelKey: "toolkit.related.goldenVisa",
      href: {
        en: DESTINATION_ROUTES.portugal.visas.golden.en,
        pt: DESTINATION_ROUTES.portugal.visas.golden.pt,
      },
    },
  },
  {
    id: "advisor",
    href: { en: "/contact", pt: "/contato" },
    relatedVisa: {
      labelKey: "toolkit.related.startup",
      href: {
        en: DESTINATION_ROUTES.portugal.visas.startup.en,
        pt: DESTINATION_ROUTES.portugal.visas.startup.pt,
      },
    },
  },
];

export function getActiveDestinations(): Destination[] {
  return DESTINATIONS.filter((d) => d.status === "active");
}

export function getFlagshipDestination(): Destination {
  return DESTINATIONS.find((d) => d.isFlagship) ?? DESTINATIONS[0];
}

export function getDestination(id: string): Destination | undefined {
  return DESTINATIONS.find((d) => d.id === id);
}

export function getDestinationStatusLabelKey(status: DestinationStatus): string {
  switch (status) {
    case "active":
      return "destinations.status.active";
    case "overview":
      return "destinations.status.overview";
    case "coming_soon":
      return "destinations.status.comingSoon";
  }
}

export function getDestinationHref(destination: Destination, lang: SiteLanguage): string {
  const routes = DESTINATION_ROUTES[destination.id as keyof typeof DESTINATION_ROUTES];
  if (routes) {
    return getLocalizedPath(routes.hub, lang);
  }
  if (destination.hubSectionId) {
    return `/pathways#${destination.hubSectionId}`;
  }
  return `/interactive?destination=${destination.id}`;
}

export function getDestinationCtaKey(destination: Destination): string {
  if (destination.status === "active") {
    return "destinations.explorePathways";
  }
  if (destination.status === "overview") {
    return `pathways.${destination.id}.exploreCta`;
  }
  return "destinations.registerInterest";
}

export function isDestinationFeatured(status: DestinationStatus): boolean {
  return status === "active" || status === "overview";
}
